import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

import apiService from '../../services/api';
import { S3Object } from '../../types/api';
import Logger from '../../utils/logger';
import { getApiErrorMessage } from '../../constants/messages';

interface ObjectsExplorerProps {
  bucketId: string;
}

const PAGE_SIZE = 100;

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 0 ? 2 : 0)} ${sizes[i]}`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
};

interface UploadJob {
  id: string;
  filename: string;
  loaded: number;
  total: number;
  status: 'uploading' | 'done' | 'error';
  errorMsg?: string;
}

const ObjectsExplorer: React.FC<ObjectsExplorerProps> = ({ bucketId }) => {
  const { t } = (useTranslation as any)('nubis3');
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [prefix, setPrefix] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploads, setUploads] = useState<UploadJob[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadObjects = useCallback(async (resetCursor = true) => {
    try {
      if (resetCursor) setLoading(true); else setLoadingMore(true);
      const data = await apiService.listS3Objects(bucketId, {
        prefix,
        cursor: resetCursor ? '' : (nextCursor ?? ''),
        max: PAGE_SIZE,
      });
      setObjects((prev) => resetCursor ? data.objects : [...prev, ...data.objects]);
      setNextCursor(data.next_cursor);
    } catch (err) {
      Logger.error('list objects failed', err);
      toast.error(t('files.errors.load'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [bucketId, prefix, nextCursor, t]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadObjects(true); }, [bucketId, prefix]);

  // ============================ Upload ============================

  const uploadFile = useCallback(async (file: File) => {
    const jobId = `${file.name}-${Date.now()}-${Math.random()}`;
    setUploads((prev) => [...prev, { id: jobId, filename: file.name, loaded: 0, total: file.size, status: 'uploading' }]);

    try {
      const finalKey = prefix ? `${prefix}${file.name}` : file.name;
      const { url } = await apiService.getS3UploadUrl(bucketId, {
        key: finalKey,
        content_type: file.type || undefined,
      });

      // PUT direct vers B2 via XHR (pour avoir progress)
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        if (file.type) xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploads((prev) => prev.map((u) => u.id === jobId ? { ...u, loaded: e.loaded } : u));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });

      setUploads((prev) => prev.map((u) => u.id === jobId ? { ...u, status: 'done', loaded: file.size } : u));
      // Refresh la liste pour faire apparaître le nouveau fichier
      loadObjects(true);
    } catch (err) {
      Logger.error('upload failed', err);
      const msg = getApiErrorMessage(err, 'unknown');
      setUploads((prev) => prev.map((u) => u.id === jobId ? { ...u, status: 'error', errorMsg: msg } : u));
      toast.error(t('files.errors.upload'));
    }
  }, [bucketId, prefix, loadObjects, t]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    Array.from(files).forEach(uploadFile);
  }, [uploadFile]);

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = ''; // reset pour pouvoir re-sélectionner le même fichier
    }
  };

  // ============================ Download ============================

  const handleDownload = async (obj: S3Object) => {
    try {
      const { url } = await apiService.getS3DownloadUrl(bucketId, obj.key);
      // Force le téléchargement via lien temporaire (le presigned URL retourne déjà
      // le bon Content-Disposition côté B2 si l'app a uploadé avec).
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      // Note: B2 ne respecte pas toujours `download` attribute pour les presigned URLs
      // cross-origin, mais le navigateur va quand même télécharger pour la plupart des types.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      Logger.error('download URL failed', err);
      toast.error(t('files.errors.download'));
    }
  };

  // ============================ Delete ============================

  const handleDelete = async (obj: S3Object) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(t('files.delete.confirm', { key: obj.key }))) return;
    try {
      await apiService.deleteS3Object(bucketId, obj.key);
      setObjects((prev) => prev.filter((o) => o.key !== obj.key));
      toast.success(t('files.delete.success'));
    } catch (err) {
      Logger.error('delete failed', err);
      toast.error(t('files.errors.delete'));
    }
  };

  // ============================ Drag & drop ============================

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // ============================ Search ============================

  const onSubmitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPrefix(searchInput);
  };

  // ============================ Render ============================

  return (
    <div
      className={`relative rounded-lg border ${dragging ? 'border-accent-500 ring-2 ring-accent-500/40' : 'border-border-1'} bg-surface-1`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border-1 flex-wrap">
        <form onSubmit={onSubmitSearch} className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('files.searchPlaceholder') as string}
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border-1 bg-surface-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-500"
            />
          </div>
          {prefix && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setPrefix(''); }}
              className="px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              {t('files.clearFilter')}
            </button>
          )}
        </form>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadObjects(true)}
            disabled={loading}
            className="px-3 py-1.5 rounded-md text-sm text-text-secondary hover:bg-surface-2 flex items-center gap-1"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('files.refresh')}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onFileInputChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-3 py-1.5 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 flex items-center gap-2"
          >
            <ArrowUpTrayIcon className="w-4 h-4" /> {t('files.upload')}
          </button>
        </div>
      </div>

      {/* Upload progress list */}
      {uploads.length > 0 && (
        <div className="px-5 py-3 border-b border-border-1 space-y-2 bg-surface-2/50">
          {uploads.map((u) => (
            <div key={u.id} className="text-xs">
              <div className="flex justify-between text-text-secondary">
                <span className="truncate flex-1 mr-2">{u.filename}</span>
                <span>
                  {u.status === 'done'
                    ? t('files.uploadDone')
                    : u.status === 'error'
                    ? `${t('files.uploadFailed')}: ${u.errorMsg}`
                    : `${formatBytes(u.loaded)} / ${formatBytes(u.total)}`}
                </span>
              </div>
              <div className="h-1 bg-surface-3 rounded mt-1 overflow-hidden">
                <div
                  className={`h-full transition-all ${u.status === 'error' ? 'bg-red-500' : u.status === 'done' ? 'bg-green-500' : 'bg-accent-500'}`}
                  style={{ width: `${u.total > 0 ? Math.min(100, (u.loaded / u.total) * 100) : 0}%` }}
                />
              </div>
            </div>
          ))}
          {uploads.some((u) => u.status !== 'uploading') && (
            <button
              type="button"
              onClick={() => setUploads((prev) => prev.filter((u) => u.status === 'uploading'))}
              className="text-xs text-text-secondary hover:text-text-primary"
            >
              {t('files.clearUploads')}
            </button>
          )}
        </div>
      )}

      {/* Drop overlay */}
      {dragging && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none bg-accent-500/5">
          <div className="bg-surface-1 border-2 border-dashed border-accent-500 rounded-lg px-8 py-6 text-center">
            <ArrowUpTrayIcon className="w-10 h-10 text-accent-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-text-primary">{t('files.dropHere')}</p>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="p-10 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500" />
        </div>
      ) : objects.length === 0 ? (
        <div className="p-12 text-center">
          <DocumentIcon className="w-10 h-10 text-text-secondary mx-auto mb-2 opacity-50" />
          <p className="text-sm text-text-secondary">{t('files.empty')}</p>
          <p className="text-xs text-text-secondary mt-1">{t('files.emptyHint')}</p>
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-text-secondary text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left font-medium">{t('files.table.name')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('files.table.size')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('files.table.type')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('files.table.uploadedAt')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('files.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-1">
              {objects.map((o) => (
                <tr key={o.key} className="hover:bg-surface-2/50">
                  <td className="px-4 py-2 text-text-primary font-mono text-xs break-all">{o.key}</td>
                  <td className="px-4 py-2 text-text-secondary">{formatBytes(o.size)}</td>
                  <td className="px-4 py-2 text-text-secondary text-xs">{o.content_type || '—'}</td>
                  <td className="px-4 py-2 text-text-secondary text-xs">{formatDate(o.uploaded_at)}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => handleDownload(o)}
                      className="p-1 text-text-secondary hover:text-accent-500"
                      aria-label="download"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(o)}
                      className="p-1 text-text-secondary hover:text-red-500 ml-1"
                      aria-label="delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextCursor && (
            <div className="p-4 border-t border-border-1 text-center">
              <button
                type="button"
                onClick={() => loadObjects(false)}
                disabled={loadingMore}
                className="px-4 py-2 rounded-md text-sm text-text-secondary hover:bg-surface-2"
              >
                {loadingMore ? t('files.loadingMore') : t('files.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ObjectsExplorer;
